import { BATCH_ACTIONS, BATCH_TOOLS } from 'pages/Batch/configs';
import type { ISubAction } from 'pages/Batch/utils/analytics';
import { buildAdjustSubTools, buildCropSubTools, buildResizeSubTools, buildWatermarkSubTools, SubToolNames } from 'pages/Batch/utils/analytics';
import type { IWfxEffect } from 'pages/Batch/sections/Sidebar/WfxTool/constants';
import { WFX_CATEGORIES, buildWfxSubTools } from 'pages/Batch/sections/Sidebar/WfxTool/constants';
import { buildBackgroundSubTools } from 'pages/Batch/sections/Sidebar/EditorRemoveBgTool/MobilePanel/constants';
import type { ObjectPosition } from 'pages/Batch/sections/Sidebar/EditorRemoveBgTool/MobilePanel/constants';
import { SMART_ADJUSTMENT_EFFECTS, BACKGROUND_BLUR_EFFECTS } from 'pages/Batch/sections/Sidebar/AIEnhanceTool/enhanceEffects';

import type { IPresetStep } from './types';

const STEP_TOOL_BY_ACTION: Partial<Record<BATCH_ACTIONS, BATCH_TOOLS>> = {
  [BATCH_ACTIONS.removeBackground]: BATCH_TOOLS.background,
  [BATCH_ACTIONS.applyBackground]: BATCH_TOOLS.background,
  [BATCH_ACTIONS.generateAIBackground]: BATCH_TOOLS.background,
  [BATCH_ACTIONS.aiEnhance]: BATCH_TOOLS.enhance,
  [BATCH_ACTIONS.composedEnhance]: BATCH_TOOLS.enhance,
  [BATCH_ACTIONS.applyCrop]: BATCH_TOOLS.crop,
  [BATCH_ACTIONS.watermark]: BATCH_TOOLS.watermark,
  [BATCH_ACTIONS.resize]: BATCH_TOOLS.resize,
  [BATCH_ACTIONS.wfx]: BATCH_TOOLS.wfx,
  [BATCH_ACTIONS.adjust]: BATCH_TOOLS.adjust,
};

function findWfxEffect(effectId: string) {
  return WFX_CATEGORIES.flatMap(c => c.effects).find(e => e.id === effectId) ?? null;
}

function buildAdjustForStep(step: IPresetStep): ISubAction[] {
  const params = (step.params as { params?: Record<string, number> }).params ?? {};
  return buildAdjustSubTools(params);
}

function buildWfxForStep(step: IPresetStep): ISubAction[] {
  const { effect, overlayResourceUrl } = step.params as { effect?: IWfxEffect | string | null; overlayResourceUrl?: string | null };
  let wfxEffect: IWfxEffect | null = null;
  if (typeof effect === 'string') {
    wfxEffect = findWfxEffect(effect);
  } else if (effect && typeof effect === 'object') {
    wfxEffect = findWfxEffect(effect.id) ?? effect;
  }
  return buildWfxSubTools(wfxEffect, overlayResourceUrl ?? null);
}

function buildCropForStep(step: IPresetStep): ISubAction[] {
  const { templateTitle, rotate } = step.params as { templateTitle?: string; rotate?: number };
  const synthetic: ISubAction[] = [];
  if (templateTitle) synthetic.push({ name: SubToolNames.crop, value: templateTitle });
  if (rotate !== undefined && rotate !== 0) synthetic.push({ name: SubToolNames.rotate, value: `${rotate}°` });
  return buildCropSubTools(synthetic);
}

function buildResizeForStep(step: IPresetStep): ISubAction[] {
  const { usePadding, bgColor } = step.params as { usePadding?: boolean; bgColor?: string | null };
  const synthetic: ISubAction[] = [];
  if (usePadding !== undefined) synthetic.push({ name: SubToolNames.usePadding, value: usePadding });
  if (bgColor) synthetic.push({ name: SubToolNames.paddingColor, value: bgColor });
  return buildResizeSubTools(synthetic, null);
}

function buildBackgroundForSteps(steps: IPresetStep[]): ISubAction[] {
  const synthetic: ISubAction[] = [];
  let position: ObjectPosition | null = null;

  steps.forEach(step => {
    if (step.action === BATCH_ACTIONS.generateAIBackground) {
      const { prompt } = step.params as { prompt?: string };
      if (prompt) synthetic.push({ name: SubToolNames.suggested, value: prompt });
    } else if (step.action === BATCH_ACTIONS.applyBackground) {
      const stepPosition = (step.params as { objectPosition?: ObjectPosition | null }).objectPosition ?? null;
      if (stepPosition) position = stepPosition;
    }
  });

  return buildBackgroundSubTools(synthetic, position);
}

function buildEnhanceForSteps(steps: IPresetStep[]): ISubAction[] {
  const composed = steps.find(s => s.action === BATCH_ACTIONS.composedEnhance);
  if (!composed) return [];

  const {
    enhanceEffectId,
    enhanceIntensity = 0,
    blurEffectId,
    blurIntensity = 0,
  } = composed.params as {
    enhanceEffectId?: string | null;
    enhanceIntensity?: number;
    blurEffectId?: string | null;
    blurIntensity?: number;
  };

  const result: ISubAction[] = [];

  if (enhanceEffectId) {
    const effect = Object.values(SMART_ADJUSTMENT_EFFECTS).find(e => e.effectId === enhanceEffectId);
    result.push({
      name: 'smart_adjustments',
      value: { item: effect?.name ?? enhanceEffectId, intensity: `${enhanceIntensity}%` },
    });
  }

  if (blurEffectId) {
    const effect = Object.values(BACKGROUND_BLUR_EFFECTS).find(e => e.effectId === blurEffectId);
    result.push({
      name: 'bg_blur',
      value: { item: effect?.name ?? blurEffectId, intensity: `${blurIntensity}%` },
    });
  }

  return result;
}

function buildWatermarkForStep(step: IPresetStep): ISubAction[] {
  const { position, opacity, size, rotateAngle } = step.params as {
    position?: string | null;
    opacity?: number;
    size?: number;
    rotateAngle?: number;
  };

  const synthetic: ISubAction[] = [];
  if (position) synthetic.push({ name: SubToolNames.position, value: position });
  if (opacity !== undefined) synthetic.push({ name: SubToolNames.opacity, value: `${opacity}%` });
  if (size !== undefined) synthetic.push({ name: SubToolNames.size, value: `${size}%` });
  if (rotateAngle !== undefined) synthetic.push({ name: SubToolNames.rotate, value: `${rotateAngle}°` });

  return buildWatermarkSubTools(synthetic);
}

function buildToolSubTools(tool: BATCH_TOOLS, steps: IPresetStep[]): ISubAction[] {
  switch (tool) {
    case BATCH_TOOLS.adjust:
      return steps.flatMap(buildAdjustForStep);
    case BATCH_TOOLS.wfx:
      return steps.flatMap(buildWfxForStep);
    case BATCH_TOOLS.crop:
      return steps.flatMap(buildCropForStep);
    case BATCH_TOOLS.resize:
      return steps.flatMap(buildResizeForStep);
    case BATCH_TOOLS.background:
      return buildBackgroundForSteps(steps);
    case BATCH_TOOLS.enhance:
      return buildEnhanceForSteps(steps);
    case BATCH_TOOLS.watermark:
      return steps.flatMap(buildWatermarkForStep);
    default:
      return [];
  }
}

export function buildPresetSubToolsByTool(steps: IPresetStep[]): Map<BATCH_TOOLS, ISubAction[]> {
  const byTool = new Map<BATCH_TOOLS, IPresetStep[]>();
  steps.forEach(step => {
    const tool = STEP_TOOL_BY_ACTION[step.action];
    if (!tool) return;
    const list = byTool.get(tool) ?? [];
    list.push(step);
    byTool.set(tool, list);
  });

  const result = new Map<BATCH_TOOLS, ISubAction[]>();
  byTool.forEach((toolSteps, tool) => {
    result.set(tool, buildToolSubTools(tool, toolSteps));
  });

  return result;
}
